import { useEffect, useRef, useState } from "react";
import { getAuthToken, SUPABASE_ANON_KEY, SUPABASE_URL } from "../lib/supabase";
import { loadGoogleMaps } from "../lib/maps";
import { useIsMobile } from "../lib/useIsMobile";

// Local YYYY-MM-DD — toISOString() is UTC, which from ~8pm ET reads as TOMORROW and
// made the live map fetch an empty job set (today's client pins + legend vanished).
const ymd = (dt) => `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,"0")}-${String(dt.getDate()).padStart(2,"0")}`;

export function FleetMap({ vehicles }) {
  const isMobile = useIsMobile();
  const [todayClients, setTodayClients] = useState([]);
  const mapRef  = useRef(null);
  const mapObj  = useRef(null);
  const markers = useRef([]);
  const COLORS  = ["#1E40AF","#B45309","#065F46","#6D28D9","#9D174D"];

  useEffect(()=>{
    const today = ymd(new Date());
    // Get today's jobs with client coords
    fetch(`${SUPABASE_URL}/rest/v1/jobs?scheduled_date=eq.${today}&select=id,client_id,client_name,client_address,status,technician_name`,{
      headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}
    }).then(r=>r.json()).then(async jobs=>{
      if (!Array.isArray(jobs)||!jobs.length) return;
      const ids = [...new Set(jobs.map(j=>j.client_id))].join(",");
      const clients = await fetch(`${SUPABASE_URL}/rest/v1/clients?id=in.(${ids})&lat=not.is.null&select=id,name,lat,lng`,{
        headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}
      }).then(r=>r.json());
      if (Array.isArray(clients)) {
        const merged = jobs.map(j=>({ ...j, lat:clients.find(c=>c.id===j.client_id)?.lat, lng:clients.find(c=>c.id===j.client_id)?.lng })).filter(j=>j.lat&&j.lng);
        setTodayClients(merged);
      }
    });
  },[]); // eslint-disable-line react-hooks/exhaustive-deps

  function plotVehicles(map, validVehicles) {
    markers.current.forEach(m=>{ try{m.infoWindow?.close();m.setMap(null);}catch(e){} });
    markers.current = [];
    const bounds = new window.google.maps.LatLngBounds();
    validVehicles.forEach((v,i)=>{
      const pos  = { lat: Number(v.stats.location.lat), lng: Number(v.stats.location.lon) };
      const name = v.nickName||v.model?.name||"Vehicle";
      const isRunning = v.stats?.isRunning||v.stats?.speed>0;
      const color = COLORS[i%COLORS.length];
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40"><circle cx="20" cy="20" r="19" fill="${color}" stroke="white" stroke-width="2"/><text x="20" y="26" text-anchor="middle" font-size="18">🚗</text>${isRunning?'<circle cx="31" cy="9" r="6" fill="#22C55E" stroke="white" stroke-width="2"/>':''}</svg>`;
      const marker = new window.google.maps.Marker({
        position: pos, map,
        title: name,
        icon: { url:`data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`, scaledSize:new window.google.maps.Size(40,40), anchor:new window.google.maps.Point(20,20) },
      });
      const iw = new window.google.maps.InfoWindow({ content:`<div style="font-family:sans-serif;padding:4px;min-width:180px"><div style="font-weight:700;font-size:14px;color:#072C76">${name}</div><div style="font-size:12px;color:#42526e">${v.model?.year} ${v.model?.make} ${v.model?.name}</div><div style="font-size:11px;margin-top:4px">📍 ${v.stats.location.address||""}</div><div style="font-size:11px;margin-top:4px">${isRunning?"🟢 Running":"⚫ Parked"}</div><div style="font-size:11px;color:#8993a4">Odometer: ${Math.round(v.stats.odometer||0).toLocaleString()} mi</div><div style="font-size:11px;color:#8993a4">Updated: ${new Date(v.stats.lastUpdated).toLocaleTimeString()}</div></div>` });
      marker.addListener("click",()=>{ markers.current.forEach(m=>m._iw?.close()); iw.open(map,marker); });
      marker._iw = iw;
      markers.current.push(marker);
      bounds.extend(pos);
    });
    if (validVehicles.length>1) map.fitBounds(bounds);
    // setCenter takes a LatLngLiteral — passing a function threw InvalidValueError (swallowed by
    // the loader's .catch) and skipped plotTodayClients whenever exactly one vehicle had a fix.
    else if (validVehicles[0]) map.setCenter({lat:Number(validVehicles[0].stats.location.lat),lng:Number(validVehicles[0].stats.location.lon)});
  }

  function plotTodayClients(map, jobs) {
    if (!map || !jobs.length) return;
    if (!map._clientMarkers) map._clientMarkers = [];
    map._clientMarkers.forEach(m=>{ try{m._iw?.close();m.setMap(null);}catch(e){} });
    map._clientMarkers = [];

    jobs.forEach(job=>{
      const pos   = { lat:Number(job.lat), lng:Number(job.lng) };
      const done  = job.status==="completed";
      const started = job.status==="started";
      const bg    = done?"#22C55E":started?"#F59E0B":"#072C76";
      const icon  = done?"✓":started?"●":"🐕";
      const svg   = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
        <circle cx="16" cy="16" r="15" fill="${bg}" stroke="white" stroke-width="2"/>
        <text x="16" y="21" text-anchor="middle" font-size="${done||started?14:12}">${icon}</text>
      </svg>`;
      const marker = new window.google.maps.Marker({
        position:pos, map, title:job.client_name, zIndex:5,
        icon:{ url:`data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`, scaledSize:new window.google.maps.Size(32,32), anchor:new window.google.maps.Point(16,16) },
      });
      const iw = new window.google.maps.InfoWindow({
        content:`<div style="font-family:sans-serif;padding:4px;min-width:160px">
          <div style="font-weight:700;font-size:13px;color:#072C76">${job.client_name}</div>
          <div style="font-size:11px;color:#42526e;margin-top:2px">📍 ${job.client_address||""}</div>
          <div style="font-size:11px;margin-top:4px;font-weight:600;color:${bg}">${done?"✓ Completed":started?"● In Progress":"○ Scheduled"}</div>
          ${job.technician_name?`<div style="font-size:11px;color:#8993a4">Tech: ${job.technician_name}</div>`:""}
        </div>`
      });
      marker.addListener("click",()=>{ map._clientMarkers.forEach(m=>m._iw?.close()); iw.open(map,marker); });
      marker._iw = iw;
      map._clientMarkers.push(marker);
    });
  }

  useEffect(()=>{
    const validVehicles = vehicles.filter(v=>v.stats?.location?.lat&&v.stats?.location?.lon);
    if (!validVehicles.length||!mapRef.current) return;

    function initAndPlot() {
      if (!window.google?.maps||!mapRef.current) return;
      if (!mapObj.current) {
        const firstPos = { lat:Number(validVehicles[0].stats.location.lat), lng:Number(validVehicles[0].stats.location.lon) };
        mapObj.current = new window.google.maps.Map(mapRef.current, {
          center: firstPos, zoom:10, mapTypeId:"roadmap",
          // greedy: one finger pans the map on a phone (cooperative shows the "use two
          // fingers" overlay and scrolls the page instead — the map felt dead).
          gestureHandling: isMobile ? "greedy" : "auto",   // one-finger pan on phones; desktop unchanged
          styles:[{featureType:"poi",elementType:"labels",stylers:[{visibility:"off"}]}],
        });
      }
      plotVehicles(mapObj.current, validVehicles);
      plotTodayClients(mapObj.current, todayClients);
    }

    // Load Google Maps if not already loaded
    loadGoogleMaps().then(initAndPlot).catch(e=>console.error("Maps load error:",e));
  },[vehicles, todayClients]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      {todayClients.length>0&&(
        <div style={{display:"flex",gap:16,fontSize:11,color:"#8993a4",marginBottom:8,flexWrap:"wrap"}}>
          <span style={{fontWeight:600,color:"#42526e"}}>{"Today's Jobs:"}</span>
          <span>🐕 Scheduled ({todayClients.filter(j=>j.status==="scheduled").length})</span>
          <span style={{color:"#F59E0B",fontWeight:600}}>● In Progress ({todayClients.filter(j=>j.status==="started").length})</span>
          <span style={{color:"#22C55E",fontWeight:600}}>✓ Completed ({todayClients.filter(j=>j.status==="completed").length})</span>
        </div>
      )}
      {/* Phone: cap the map so the user can still reach the page around it (greedy
          gestures mean a drag ON the map never scrolls the page). */}
      <div ref={mapRef} style={{width:"100%",height:isMobile?"min(420px, 55vh)":"420px",borderRadius:8,background:"#e8eaed"}}/>
    </div>
  );
}
